import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Leaf, ArrowLeft, Loader2, Package, DollarSign, MapPin, 
  Calendar, Info, Sprout, FileText
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SellSeedsPage = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    plant_name: "",
    variety: "",
    description: "",
    price: "",
    quantity_available: "",
    seed_count_per_packet: "",
    year_harvested: new Date().getFullYear(),
    origin_location: "",
    growing_instructions: "",
    planting_zone: "",
    days_to_germination: "",
    is_organic: false,
    is_heirloom: false
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error("Please sign in to list seeds");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price),
        quantity_available: parseInt(formData.quantity_available),
        seed_count_per_packet: parseInt(formData.seed_count_per_packet),
        year_harvested: parseInt(formData.year_harvested)
      };

      await axios.post(`${API}/marketplace/listings`, payload, {
        withCredentials: true
      });

      toast.success("Seeds listed successfully!");
      navigate("/marketplace");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create listing");
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full card-organic">
          <CardContent className="pt-8 text-center">
            <Package className="w-16 h-16 mx-auto mb-4 text-primary/50" />
            <h2 className="font-serif text-2xl font-bold mb-2">Sign In to Sell</h2>
            <p className="text-muted-foreground mb-6">
              Create an account to start selling your seeds on the marketplace.
            </p>
            <Button onClick={login} className="btn-primary">
              Sign In with Google
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border/50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/marketplace" className="text-muted-foreground hover:text-primary transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="font-serif text-xl font-bold text-foreground">
              List Your Seeds
            </h1>
          </div>
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="RootSpace.Pro" className="h-8 w-auto" />
            
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="card-organic">
            <CardHeader>
              <CardTitle className="font-serif flex items-center gap-2">
                <Sprout className="w-5 h-5 text-primary" />
                Seed Listing Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Info */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Plant Name *</Label>
                    <Input
                      name="plant_name"
                      value={formData.plant_name}
                      onChange={handleChange}
                      placeholder="e.g., Tomato"
                      required
                      data-testid="plant-name"
                    />
                  </div>
                  <div>
                    <Label>Variety</Label>
                    <Input
                      name="variety"
                      value={formData.variety}
                      onChange={handleChange}
                      placeholder="e.g., Cherokee Purple"
                    />
                  </div>
                </div>

                <div>
                  <Label>Description *</Label>
                  <Textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Describe your seeds, their history, and what makes them special..."
                    rows={4}
                    required
                    data-testid="description"
                  />
                </div>

                {/* Pricing & Quantity */}
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label className="flex items-center gap-1">
                      <DollarSign className="w-4 h-4" />
                      Price per Packet *
                    </Label>
                    <Input
                      name="price"
                      type="number"
                      step="0.01"
                      min="0.50"
                      value={formData.price}
                      onChange={handleChange}
                      placeholder="3.50"
                      required
                      data-testid="price"
                    />
                  </div>
                  <div>
                    <Label>Packets Available *</Label>
                    <Input
                      name="quantity_available"
                      type="number"
                      min="1"
                      value={formData.quantity_available}
                      onChange={handleChange}
                      placeholder="10"
                      required
                    />
                  </div>
                  <div>
                    <Label>Seeds per Packet *</Label>
                    <Input
                      name="seed_count_per_packet"
                      type="number"
                      min="1"
                      value={formData.seed_count_per_packet}
                      onChange={handleChange}
                      placeholder="25"
                      required
                    />
                  </div>
                </div>

                {/* Origin & Year */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      Origin Location *
                    </Label>
                    <Input
                      name="origin_location"
                      value={formData.origin_location}
                      onChange={handleChange}
                      placeholder="e.g., Austin, TX"
                      required
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      Year Harvested *
                    </Label>
                    <Input
                      name="year_harvested"
                      type="number"
                      min="2020"
                      max={new Date().getFullYear()}
                      value={formData.year_harvested}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                {/* Growing Info */}
                <div>
                  <Label className="flex items-center gap-1">
                    <FileText className="w-4 h-4" />
                    Growing Instructions *
                  </Label>
                  <Textarea
                    name="growing_instructions"
                    value={formData.growing_instructions}
                    onChange={handleChange}
                    placeholder="Include planting depth, spacing, light requirements, and any special care tips..."
                    rows={4}
                    required
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Planting Zone</Label>
                    <Input
                      name="planting_zone"
                      value={formData.planting_zone}
                      onChange={handleChange}
                      placeholder="e.g., 5-9"
                    />
                  </div>
                  <div>
                    <Label>Days to Germination</Label>
                    <Input
                      name="days_to_germination"
                      value={formData.days_to_germination}
                      onChange={handleChange}
                      placeholder="e.g., 7-14 days"
                    />
                  </div>
                </div>

                {/* Badges */}
                <div className="flex gap-6">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="is_organic"
                      name="is_organic"
                      checked={formData.is_organic}
                      onCheckedChange={(checked) => setFormData(p => ({...p, is_organic: checked}))}
                    />
                    <Label htmlFor="is_organic" className="cursor-pointer">
                      Organic (no synthetic chemicals)
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="is_heirloom"
                      name="is_heirloom"
                      checked={formData.is_heirloom}
                      onCheckedChange={(checked) => setFormData(p => ({...p, is_heirloom: checked}))}
                    />
                    <Label htmlFor="is_heirloom" className="cursor-pointer">
                      Heirloom Variety
                    </Label>
                  </div>
                </div>

                {/* Commission Notice */}
                <div className="bg-muted/50 p-4 rounded-lg flex items-start gap-3">
                  <Info className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-muted-foreground">
                    <p className="font-medium text-foreground mb-1">Marketplace Commission</p>
                    <p>
                      ROOTSPACE takes an 18% commission on each sale to maintain the platform. 
                      Payment is held until you ship the order and provide tracking information.
                    </p>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full btn-primary"
                  disabled={loading}
                  data-testid="submit-listing"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating Listing...
                    </>
                  ) : (
                    "List Seeds for Sale"
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </main>
    </div>
  );
};

export default SellSeedsPage;
